import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { Chart } from "chart.js";

import { numero, pct } from "../bsc-formato";
import { COLOR_BSC, GraficaBsc } from "./grafica-bsc";

/** Las tres rebanadas como se dibujan: suman 1. */
interface Rebanadas {
  alcanzado: number;
  noCumplido: number;
  porCumplir: number;
}

/**
 * BSC · pastel «ESTATUS GERENCIAL» del FOR-011 (A1, docs/BSC.md §7 gráfica
 * 2): alcanzado = E_q, no cumplido = lo programado al corte que falta
 * (EP_q − E_q) y por cumplir = lo que resta del año; en el centro el alcance
 * y el objetivo Solser.
 *
 * El servidor manda las tres partes tal cual §3 (porCumplir = 1 − EP_q), que
 * suman más de 1 cuando el alcance va por delante de lo programado
 * (BscCalculadora.calcularEmpresa lo advierte). La dona necesita que sumen 1:
 * el alcanzado se topa en 100 % y «por cumplir» es lo que resta tras
 * alcanzado y no cumplido. Cuando E_q ≤ EP_q eso es exactamente lo del
 * servidor; la leyenda dice las mismas cifras que se dibujan, salvo el
 * alcanzado, que en la leyenda, el tooltip y el texto accesible va sin
 * topar, como el centro (109 % es sobrecumplimiento, aunque la rebanada
 * llene la dona).
 */
@Component({
  selector: "app-pastel-gerencial-bsc",
  standalone: true,
  template: `
    <div class="bsc-pastel">
      <div class="bsc-pastel__dona">
        <canvas #lienzo role="img" [attr.aria-label]="textoAccesible"></canvas>
        <div class="bsc-pastel__centro" aria-hidden="true">
          <strong>{{ textoAlcance }}</strong>
          <span>{{ textoCentro }}</span>
        </div>
      </div>
      <ul class="bsc-grafica-leyenda">
        <li><i class="bsc-grafica-leyenda__barra is-real"></i>Alcanzado<strong>{{ textoAlcance }}</strong></li>
        <li><i class="bsc-grafica-leyenda__barra is-amarillo"></i>No cumplido<strong>{{ textoRebanada("noCumplido") }}</strong></li>
        <li><i class="bsc-grafica-leyenda__barra is-pista"></i>Por cumplir<strong>{{ textoRebanada("porCumplir") }}</strong></li>
      </ul>
    </div>
  `,
  styleUrls: ["./grafica-bsc.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PastelGerencialBscComponent extends GraficaBsc {
  /** E_q al corte (fracción). */
  @Input() alcanzado: number | null = null;
  /** max(0, EP_q − E_q). */
  @Input() noCumplido: number | null = null;
  /** max(0, 1 − EP_q). */
  @Input() porCumplir: number | null = null;
  /** Objetivo Solser del corte; nulo = no se dice. */
  @Input() objetivo: number | null = null;

  /** Nulo si no hay alcance que dibujar. */
  get rebanadas(): Rebanadas | null {
    const alcance = numero(this.alcanzado);
    if (alcance == null) {
      return null;
    }
    const alcanzado = Math.min(1, Math.max(0, alcance));
    const noCumplido = Math.min(1 - alcanzado, Math.max(0, numero(this.noCumplido) ?? 0));
    return { alcanzado, noCumplido, porCumplir: Math.max(0, 1 - alcanzado - noCumplido) };
  }

  get textoAlcance(): string {
    return pct(this.alcanzado);
  }

  get textoCentro(): string {
    return this.objetivo != null ? `objetivo ${pct(this.objetivo)}` : "alcance";
  }

  textoRebanada(parte: keyof Rebanadas): string {
    const r = this.rebanadas;
    return r ? pct(r[parte]) : pct(null);
  }

  get textoAccesible(): string {
    const r = this.rebanadas;
    if (!r) {
      return "Estatus gerencial: sin alcance al corte.";
    }
    const objetivo = this.objetivo != null ? `; objetivo Solser ${pct(this.objetivo)}` : "";
    return `Estatus gerencial: alcanzado ${this.textoAlcance}, no cumplido ${pct(r.noCumplido)}, por cumplir ${pct(r.porCumplir)}${objetivo}.`;
  }

  protected arma(lienzo: HTMLCanvasElement): Chart | null {
    const r = this.rebanadas;
    if (!r) {
      return null;
    }
    return new Chart<"doughnut", number[], string>(lienzo, {
      type: "doughnut",
      data: {
        labels: ["Alcanzado", "No cumplido", "Por cumplir"],
        datasets: [
          {
            data: [r.alcanzado, r.noCumplido, r.porCumplir],
            backgroundColor: [
              this.color(COLOR_BSC.real),
              this.color(COLOR_BSC.amarillo),
              this.color(COLOR_BSC.pista),
            ],
            // Solo «por cumplir» lleva filo: en cristal su gris casi es el de la tarjeta.
            borderWidth: [0, 0, 1],
            borderColor: ["transparent", "transparent", this.color(COLOR_BSC.rejilla)],
            hoverOffset: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        cutout: "72%",
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              // El alcanzado sin topar, como el centro y la leyenda.
              label: (c) => `${c.label}: ${c.dataIndex === 0 ? this.textoAlcance : pct(c.raw as number)}`,
            },
          },
        },
      },
    });
  }
}
